import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Eye, Edit3, Trash2, ExternalLink, ChevronDown, Upload, Download, Clock, CheckSquare, XSquare, X, Loader2, Send, Database, RefreshCw } from 'lucide-react';
import EavList from '../components/EavList';
import BatchUpdateModal from '../components/BatchUpdateModal';
import CategorySelector from '../components/CategorySelector';
import UserSelect from '../components/UserSelect';
import AttributeSetSelector from '../components/AttributeSetSelector';
import SupplierSelect from '../components/SupplierSelect';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';
import { useNotification } from '../context/NotificationContext';
import ResponsiblePersonsCell from '../components/ResponsiblePersonsCell';
import BatchAssignResponsibleModal from '../components/BatchAssignResponsibleModal';
import BatchRemoveResponsibleModal from '../components/BatchRemoveResponsibleModal';
import ProductQcCheckModal from './ProductQcCheckModal';
import StoreSelectorModal from './StoreSelectorModal';
import TaskProgressModal from '../components/TaskProgressModal';

/**
 * Batch QC Check Modal
 * 批量更新产品质量检查状态，支持父产品（自动包含子产品）
 */
const BatchQcCheckModal = ({ selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [selectedStatus, setSelectedStatus] = useState('pass');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(0);
  const [errors, setErrors] = useState([]);

  const statusOptions = [
    { value: 'pending', label: t('qa_pending') || 'Pending', icon: Clock, color: 'bg-amber-500' },
    { value: 'pass', label: t('qa_pass') || 'Pass', icon: CheckSquare, color: 'bg-green-500' },
    { value: 'fail', label: t('qa_fail') || 'Fail', icon: XSquare, color: 'bg-red-500' },
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors([]);
    setDone(0);
    try {
      const res = await fetch('/api/products/batch-qc-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, status: selectedStatus }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        onSuccess();
      } else {
        notify(data.message || t('common.error'), 'error');
        setErrors([data.message || t('common.error')]);
      }
    } catch (e) {
      notify(t('common.error'), 'error');
      setErrors([e.message]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t('qa_batch_update') || 'Batch QC Check'}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-sm text-slate-600 dark:text-slate-400">
            {t('qa_batch_hint', { count: selectedIds.length }) || `Update QC status for ${selectedIds.length} selected product(s).`}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('qa_select_status') || 'Select Status'}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {statusOptions.map(opt => {
                const Icon = opt.icon;
                const isSelected = selectedStatus === opt.value;
                return (
                  <button key={opt.value}
                    onClick={() => setSelectedStatus(opt.value)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-slate-400 dark:border-slate-300 bg-white dark:bg-slate-700 shadow-sm'
                        : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}>
                    <div className={"w-3 h-3 rounded-full " + opt.color} />
                    <Icon size={20} />
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Batch Push Status Update Modal
 */
const BatchPushStatusModal = ({ selectedIds, storeId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedStatus) {
      notify(t('common.select_status_hint') || '请选择目标状态', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/products/batch-update-push-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: selectedIds, store_id: storeId, status: selectedStatus }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('products.batch_update_success', { count: data.success }) || `推送状态已更新：${data.success} 个产品`, 'success');
        onSuccess();
      } else if (data.status === 'partial') {
        notify(t('products.batch_update_partial', { success: data.success, errors: data.errors?.length || 0 }) || `部分成功：${data.success} 个更新，${data.errors?.length || 0} 个失败`, 'warning');
        onSuccess();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (e) {
      notify((t('products.batch_update_failed') || '重设推送状态失败') + ': ' + e.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'pending_qa', label: t('products.push_status_pending_qa') || 'Pending QA', icon: Clock },
    { value: 'pending_push', label: t('products.push_status_pending_push') || 'Pending Push', icon: Send },
    { value: 'success', label: t('products.push_status_success') || 'Success', icon: CheckSquare },
    { value: 'failed', label: t('products.push_status_failed') || 'Failed', icon: XSquare },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t('products.batch_update_push_status') || '重设推送状态'}</h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 text-sm text-slate-600 dark:text-slate-400">
            {t('products.batch_update_hint', { count: selectedIds.length }) || `已选择 ${selectedIds.length} 个产品，请选择目标推送状态`}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('common.select_status') || '目标状态'}
            </label>
            <div className="space-y-2">
              {statusOptions.map(opt => {
                const Icon = opt.icon;
                const isSelected = selectedStatus === opt.value;
                return (
                  <button key={opt.value}
                    onClick={() => setSelectedStatus(opt.value)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                        : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}>
                    <Icon size={18} />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            {t('common.cancel') || '取消'}
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('common.save') || '确认'}
          </button>
        </div>
      </div>
    </div>
  );
};

const QcStatusBadge = ({ status, t }) => {
  const config = {
    pending: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock, label: t('qa_pending') },
    processing: { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2, label: t('qa_processing') || 'Processing' },
    pass: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckSquare, label: t('qa_pass') },
    fail: { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XSquare, label: t('qa_fail') },
  };
  const cfg = config[status];
  if (!cfg) return <span className="text-slate-300">-</span>;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const PushStatusBadge = ({ status, t }) => {
  const config = {
    processing: { bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: RefreshCw, label: t('products.push_status_processing') || '推送中' },
    pending_qa: { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock, label: t('products.push_status_pending_qa') },
    pending_push: { bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Send, label: t('products.push_status_pending_push') },
    success: { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckSquare, label: t('products.push_status_success') },
    failed: { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XSquare, label: t('products.push_status_failed') },
  };
  const cfg = config[status];
  if (!cfg) return <span className="text-slate-300">-</span>;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const ProductList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { notify } = useNotification();
  const [rpLookup, setRpLookup] = useState(null);
  const [batchAssignIds, setBatchAssignIds] = useState(null);
  const [batchRemoveIds, setBatchRemoveIds] = useState(null);
  const [batchUpdateIds, setBatchUpdateIds] = useState(null);
  const [qcModalItem, setQcModalItem] = useState(null);
  const [batchQcModal, setBatchQcModal] = useState(null);
  const [pushModalProductIds, setPushModalProductIds] = useState(null);
  const [pushStatusModal, setPushStatusModal] = useState(null);
  const [pushProgress, setPushProgress] = useState({ isOpen: false, title: '', message: '' });
  const pushProgressStep = useCallback((title, message) => {
    setPushProgress({ isOpen: true, title, message });
  }, []);
  const pushHideProgress = useCallback(() => {
    setPushProgress({ isOpen: false, title: '', message: '' });
  }, []);

  // ── 批量推送至渠道（逐个推送 + 进度条） ──
  const handleBatchPushToChannel = useCallback(async (storeId, productIds) => {
    const total = productIds.length;
    if (total === 0) return;

    // 关闭店铺选择弹窗，打开进度弹窗
    setPushModalProductIds(null);
    pushProgressStep(t('channel.push_to_channel') || '推送至渠道', `准备推送 ${total} 个产品...`);

    let success = 0;
    let fail = 0;
    const errors = [];

    for (let i = 0; i < total; i++) {
      const pid = productIds[i];
      pushProgressStep(
        t('channel.push_to_channel') || '推送至渠道',
        `正在推送 ${i + 1}/${total}...`
      );
      try {
        const res = await fetch('/api/products/batch-generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            store_id: storeId,
            product_ids: [pid],
          }),
        });
        const data = await res.json();
        // 只认实际推送成功的，跳过和失败都算失败
        if (data.status === 'success' && data.data && data.data.success > 0) {
          success++;
        } else {
          fail++;
          const reason = data.data?.skipped_details?.[0]?.message || data.message || '推送失败';
          errors.push(`#${pid}: ${reason}`);
        }
      } catch (e) {
        fail++;
        errors.push(`#${pid}: ${e.message}`);
      }
    }

    pushHideProgress();

    let msg = `完成：${success} 成功`;
    if (fail > 0) {
      msg += `，${fail} 失败`;
      if (errors.length > 0) {
        msg += `（${errors[0].substring(0, 60)}）`;
      }
    }
    notify(msg, fail === 0 ? 'success' : 'error');
    setQcRefreshKey(k => k + 1);
  }, [t, notify, pushProgressStep, pushHideProgress]);

  const [qcRefreshKey, setQcRefreshKey] = useState(0);
  const [pushStatusMap, setPushStatusMap] = useState({});
  const [selectedPushStoreId, setSelectedPushStoreId] = useState('');
  const selectedPushStoreIdRef = useRef(selectedPushStoreId);
  selectedPushStoreIdRef.current = selectedPushStoreId;

  // Push status batch update handler (must be after selectedPushStoreId declaration)
  const handleBatchUpdatePushStatus = useCallback((selectedIds) => {
    setPushStatusModal({ ids: selectedIds, storeId: parseInt(selectedPushStoreId, 10) });
  }, [selectedPushStoreId]);

  const [stores, setStores] = useState([]);

  const [allAttributes, setAllAttributes] = useState(null);

  // 加载所有属性组的全部属性（去重），用于筛选和列配置
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/products/all-attributes');
        const data = await res.json();
        if (data.status === 'success') {
          setAllAttributes(data.data);
        }
      } catch (e) {
        console.error('Failed to load all product attributes:', e);
      }
    })();
    // 加载店铺列表（用于推送状态筛选）
    (async () => {
      try {
        const res = await fetch('/api/channel/stores');
        const data = await res.json();
        if (data.status === 'success') {
          setStores(data.data.stores || []);
        }
      } catch (e) {
        console.error('Failed to load stores:', e);
      }
    })();
  }, []);

  const itemsRef = useRef([]);

  const fetchPushStatus = useCallback((productIds, storeId) => {
    if (!storeId || !productIds || productIds.length === 0) return;
    fetch('/api/products/batch-push-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_ids: productIds, store_id: parseInt(storeId, 10) }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') setPushStatusMap(data.data || {});
      })
      .catch(() => {});
  }, []);

  const afterLoad = useCallback((items) => {
    itemsRef.current = items;
    const ids = items.map(i => i.entity_id).filter(id => id != null);
    if (ids.length === 0) { setRpLookup({}); return; }
    const params = new URLSearchParams();
    ids.forEach(id => params.append('ids[]', id));
    fetch(`/api/products/batch-responsible-persons?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') setRpLookup(data.data || {});
      })
      .catch(() => {});
    // 数据加载后，如果已选中店铺则加载推送状态
    fetchPushStatus(ids, selectedPushStoreIdRef.current);
  }, [fetchPushStatus]);

  // 店铺下拉变化时（不触发数据重载），为当前列表加载推送状态
  useEffect(() => {
    const ids = itemsRef.current.map(i => i.entity_id).filter(id => id != null);
    fetchPushStatus(ids, selectedPushStoreId);
  }, [selectedPushStoreId, fetchPushStatus]);

  const [openLinkDropdownId, setOpenLinkDropdownId] = useState(null);

  // 点击下拉菜单外部时关闭
  useEffect(() => {
    if (openLinkDropdownId === null) return;
    const handler = () => setOpenLinkDropdownId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openLinkDropdownId]);

  const renderCell = useCallback((item, col) => {
    if (col.code === 'responsible_persons') {
      return (
        <ResponsiblePersonsCell
          entityType="product"
          entityId={item.entity_id}
          lookup={rpLookup}
        />
      );
    }
    if (col.code === 'reference_link') {
      const raw = col.isEav ? item.attributes?.[col.code] : item[col.code];
      if (!raw) return <span className="text-slate-300">-</span>;
      let urls = [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) urls = parsed;
        else urls = [String(parsed)];
      } catch {
        urls = [String(raw)];
      }
      urls = urls.filter(Boolean);
      if (urls.length === 0) return <span className="text-slate-300">-</span>;
      return (
        <div className="flex flex-col gap-1">
          {urls.map((url, idx) => (
            <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="text-blue-600 dark:text-blue-400 hover:underline text-xs break-all leading-tight">
              {url}
            </a>
          ))}
        </div>
      );
    }
    if (col.code === 'qc_status') {
      return <QcStatusBadge status={item.qc_status} t={t} />;
    }
    if (col.code === 'push_status') {
      const ps = pushStatusMap[item.entity_id];
      if (!ps) return <span className="text-slate-300">-</span>;
      return <PushStatusBadge status={ps.push_status} t={t} />;
    }
    return null; // fallback to default rendering for other columns
  }, [rpLookup, openLinkDropdownId, t, qcRefreshKey, pushStatusMap]);

  const flatColumns = [
    { code: 'entity_id', label: t('products.id'), inputType: 'Text Field' },
    { code: 'image',     label: t('products.image'), inputType: 'Image' },
    { code: 'sku',       label: t('products.sku'), inputType: 'Text Field' },
    { code: 'category_id', label: t('products.category_id'), inputType: 'Dropdown' },
    { code: 'attribute_set_id', label: t('products.attribute_set'), inputType: 'Text Field' },
    { code: 'item_group_id', label: t('products.parent_variant'), inputType: 'Text Field' },
    { code: 'qc_status', label: t('qa_status') || 'QC', inputType: 'Text Field' },
    { code: 'responsible_persons', label: t('products.responsible_person'), inputType: 'Text Field' },
    { code: 'created_at', label: t('products.created_at'), inputType: 'Date' },
    { code: 'updated_at', label: t('products.updated_at'), inputType: 'Date' },
    { code: 'created_by', label: t('products.created_by'), inputType: 'Text Field' },
    { code: 'supplier_id', label: t('products.supplier'), inputType: 'Text Field' },
    { code: 'push_status', label: t('products.push_status'), inputType: 'Text Field' },
  ];

  const filterExcludeCodes = ['image', 'short_description', 'description', 'shipping_package'];

  const filterCustomRenderers = {
    category_id: (col, value, onChange) => (
      <CategorySelector
        value={value ? parseInt(value, 10) : ''}
        onChange={(val) => onChange(val ? String(val) : '')}
      />
    ),
    created_by: (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    ),
    attribute_set_id: (col, value, onChange) => (
      <AttributeSetSelector
        value={value ? parseInt(value, 10) : ''}
        entityType="product"
        onChange={(val) => onChange(val ? String(val) : '')}
      />
    ),
    responsible_persons: (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    ),
    supplier_id: (col, value, onChange) => (
      <SupplierSelect value={value} onChange={onChange} />
    ),
    qc_status: (col, value, onChange) => (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || '')}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 dark:text-white"
      >
        <option value="">{t('common.all')}</option>
        <option value="pending">{t('qa_pending') || 'Pending'}</option>
        <option value="processing">{t('qa_processing') || 'Processing'}</option>
        <option value="pass">{t('qa_pass') || 'Pass'}</option>
        <option value="fail">{t('qa_fail') || 'Fail'}</option>
      </select>
    ),
    created_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          value={allFilters['created_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_from: e.target.value})}
          placeholder={t('common.from_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
        <input
          type="date"
          value={allFilters['created_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_to: e.target.value})}
          placeholder={t('common.to_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
      </div>
    ),
    updated_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          value={allFilters['updated_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_from: e.target.value})}
          placeholder={t('common.from_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
        <input
          type="date"
          value={allFilters['updated_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_to: e.target.value})}
          placeholder={t('common.to_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
      </div>
    ),
    push_status: (col, value, onChange) => (
      <select value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white">
        <option value="">{t('products.push_status_all')}</option>
        <option value="pending_qa">{t('products.push_status_pending_qa')}</option>
        <option value="pending_push">{t('products.push_status_pending_push')}</option>
        <option value="success">{t('products.push_status_success')}</option>
        <option value="failed">{t('products.push_status_failed')}</option>
      </select>
    ),
  };

  const navigate = useNavigate();
  const canAssign = hasPermission('product', 'assign');
  const fileInputRef = useRef(null);

  const canExport = hasPermission('product', 'export');
  const canImport = hasPermission('product', 'import');
  const canTemplate = canExport; // template uses the 'export' privilege

  // ── Template modal state ──
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateSets, setTemplateSets] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // ── Import / Export ──
  const handleExport = async () => {
    try {
      const res = await fetch('/api/products/export', {
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'product_export_' + new Date().toISOString().slice(0, 19).replace(/[:-]/g, '') + '.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json().catch(() => ({}));
        notify(err.message || t('common.error'), 'error');
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    }
  };

  // Open template selection modal with attribute set list
  const handleTemplateClick = async () => {
    setTemplateLoading(true);
    setShowTemplateModal(true);
    try {
      const res = await fetch('/api/products/template-sets');
      const data = await res.json();
      if (data.status === 'success') {
        setTemplateSets(data.data || []);
      } else {
        setTemplateSets([]);
        notify(t('common.error'), 'error');
      }
    } catch (err) {
      setTemplateSets([]);
      notify(t('common.error'), 'error');
    } finally {
      setTemplateLoading(false);
    }
  };

  // Download template for a specific set (or all sets if setId is null)
  const downloadTemplate = async (setId) => {
    const params = setId ? `?set_id=${setId}` : '';
    const url = `/api/products/template${params}`;
    try {
      const res = await fetch(url, {
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });
      if (res.ok) {
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const setSuffix = setId ? '_' + setId : '_all';
        a.href = blobUrl;
        a.download = 'product_import_template' + setSuffix + '_' + new Date().toISOString().slice(0, 19).replace(/[:-]/g, '') + '.xlsx';
        a.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        const text = await res.text().catch(() => '');
        console.error('Template download failed:', res.status, text);
        notify(t('common.error') + ' (' + res.status + ')', 'error');
      }
    } catch (err) {
      console.error('Template download exception:', err);
      notify(t('common.error'), 'error');
    } finally {
      setShowTemplateModal(false);
    }
  };

  const handleTemplateSetSelect = (setId) => {
    downloadTemplate(setId);
  };

  const handleTemplateAll = () => {
    downloadTemplate(null);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-imported

    setImporting(true);

    // Read as base64
    const reader = new FileReader();
    reader.onerror = () => {
      setImporting(false);
      notify(t('common.error'), 'error');
    };
    reader.onabort = () => {
      setImporting(false);
      notify(t('common.error'), 'error');
    };
    reader.onload = async (evt) => {
      const base64 = evt.target.result.split(',')[1]; // remove data:...;base64,
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 600000); // 10min timeout (image downloads can be slow)
        const res = await fetch('/api/products/import', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_content: base64 }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const data = await res.json();
        setImporting(false);
        if (data.status === 'success') {
          const created = data.data?.created || 0;
          const errCount = data.data?.errors?.length || 0;
          const summary = `✅ Imported ${created} products.${errCount > 0 ? ` (${errCount} errors)` : ''}`;
          notify(summary, errCount > 0 ? 'warning' : 'success');
          window.location.reload();
        } else {
          notify(data.message || t('common.error'), 'error');
        }
      } catch (err) {
        setImporting(false);
        if (err.name === 'AbortError') {
          notify(t('products.import_timeout'), 'error');
        } else {
          notify(t('common.error'), 'error');
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Extra toolbar buttons for import/export
  const extraToolbarButtons = (
    <>
      {canExport && (
        <button onClick={handleExport}
          className="flex items-center justify-center gap-1.5 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
          title={t('products.export')}>
          <Download size={16} />{t('products.export')}
        </button>
      )}
      {canTemplate && (
        <button onClick={handleTemplateClick}
          className="flex items-center justify-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          title={t('products.template')}>
          <Download size={16} />{t('products.template')}
        </button>
      )}
      {canImport && (
        <button onClick={handleImportClick}
          className="flex items-center justify-center gap-1.5 bg-orange-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
          title={t('products.import')}>
          <Upload size={16} />{t('products.import')}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        onChange={handleImportFile}
        style={{ display: 'none' }}
      />
    </>
  );

  const renderActions = (item, helpers) => {
    const { canView, canEdit, canDelete, handleDelete, page, pageSize } = helpers;
    const viewRoute = `/products/view/${item.entity_id}`;
    const editRoute = `/products/edit/${item.entity_id}`;

    // Parse reference links for the ExternalLink icon in actions
    const rawLink = item.reference_link ?? item.attributes?.reference_link;
    let linkUrls = [];
    if (rawLink) {
      try {
        const parsed = JSON.parse(rawLink);
        linkUrls = Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch {
        linkUrls = [String(rawLink)];
      }
      linkUrls = linkUrls.filter(Boolean);
    }
    const isLinkOpen = openLinkDropdownId === `link_${item.entity_id}`;

    return (
      <>
        <div className="relative">
          {linkUrls.length === 0 ? (
            <span className="inline-flex p-1.5 rounded-lg text-slate-300 dark:text-slate-600 cursor-not-allowed"
              title={t('products.no_reference_links') || 'No Reference Links'}>
              <ExternalLink size={16} />
            </span>
          ) : linkUrls.length === 1 ? (
            <a href={linkUrls[0]} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="inline-flex p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title={linkUrls[0]}>
              <ExternalLink size={16} />
            </a>
          ) : (
            <>
              <button onClick={(e) => {
                e.stopPropagation();
                setOpenLinkDropdownId(isLinkOpen ? null : `link_${item.entity_id}`);
              }}
                className="inline-flex items-center gap-0.5 p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                title={t('products.reference_links')}>
                <ExternalLink size={16} />
                <ChevronDown size={12} />
              </button>
              {isLinkOpen && (
                <div onClick={e => e.stopPropagation()}
                  className="absolute left-0 top-full mt-1 z-[9999] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 min-w-[160px]">
                  {linkUrls.map((url, idx) => (
                    <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title={url}>
                      <ExternalLink size={14} />
                      <span className="truncate max-w-[200px]">{url}</span>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {canView && (
          <button onClick={() => navigate(viewRoute)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title={t("common.view")}><Eye size={16} /></button>
        )}
        {hasPermission('product', 'qa_check') && (
          <button onClick={() => setQcModalItem({ entityId: item.entity_id, sku: item.sku, qcStatus: item.qc_status })}
            className={`p-1.5 rounded-lg transition-colors ${
              item.qc_status === 'pass' ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20' :
              item.qc_status === 'fail' ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' :
              'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
            }`}
            title={t('qa_status') || 'QC'}>
            <CheckSquare size={16} />
          </button>
        )}
        {canAssign && (
          <button onClick={() => navigate(`/products/${item.entity_id}/assign`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            title={t("common.batch_assign_responsible")}><UserPlus size={16} /></button>
        )}
        {canEdit && (
          <button onClick={() => navigate(editRoute, { state: { fromPage: page, fromPageSize: pageSize } })}
            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t("common.edit")}><Edit3 size={16} /></button>
        )}
        {hasPermission('product', 'generate_drafts') && (item.item_group_id > 0 ? (
          <span className="inline-flex p-1.5 rounded-lg text-slate-300 dark:text-slate-600 cursor-not-allowed"
            title={t('channel.child_push_disabled')}><Send size={16} /></span>
        ) : (
          <button onClick={() => setPushModalProductIds([item.entity_id])}
            className="p-1.5 rounded-lg text-slate-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            title={t('channel.push_to_channel')}><Send size={16} /></button>
        ))}
        {canDelete && (
          <button onClick={() => handleDelete(item.entity_id)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t("common.delete")}><Trash2 size={16} /></button>
        )}
      </>
    );
  };

  return (
    <>
    <EavList
      key={qcRefreshKey}
      entityType="products"
      flatColumns={flatColumns}
      externalAttributeConfig={allAttributes}
      defaultVisible={['entity_id', 'image', 'sku', 'name', 'cost', 'qc_status', 'push_status', 'created_at']}
      titleKey="products.title"
      searchKey="products.search"
      pageTotalKey="products.page_total"
      confirmDeleteKey="products.confirm_delete"
      confirmDeleteSingleKey="products.confirm_delete_single"
      routes={{
        add: '/products/new',
        view: (id) => `/products/view/${id}`,
        edit: (id) => `/products/edit/${id}`,
      }}
      treeField="item_group_id"
      excludeFilterColumns={filterExcludeCodes}
      usePageSizeChange={true}
      canAdd={hasPermission('product', 'add')}
      canEdit={hasPermission('product', 'edit')}
      canDelete={hasPermission('product', 'delete')}
      canView={hasPermission('product', 'view')}
      filterCustomRenderers={filterCustomRenderers}
      renderActions={renderActions}
      renderCell={renderCell}
      afterLoad={afterLoad}
      onBatchAssign={canAssign ? setBatchAssignIds : undefined}
      onBatchRemoveResponsible={canAssign ? setBatchRemoveIds : undefined}
      onBatchUpdateCustom={setBatchUpdateIds}
      onBatchQcUpdate={hasPermission('product', 'qa_check') ? setBatchQcModal : undefined}
      onBatchPushToChannel={hasPermission('product', 'generate_drafts') ? setPushModalProductIds : undefined}
      onBatchUpdatePushStatus={hasPermission('product', 'update_push_status') ? handleBatchUpdatePushStatus : undefined}
      extraToolbarButtons={extraToolbarButtons}
      filterBarExtra={(filters, setFilters) => (
        <div className="flex items-center gap-2 w-full md:w-auto" key="push-store-filter">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
            <Database size={12} className="inline mr-1" />
            {t('channel.store')}:
          </label>
          <select value={selectedPushStoreId}
            onChange={e => {
              const val = e.target.value;
              setSelectedPushStoreId(val);
              setFilters({...filters, push_store_id: val});
            }}
            className="w-full md:min-w-[130px] rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-2 text-xs dark:text-slate-200 focus:ring-2 focus:ring-blue-500">
            <option value="">{t('products.push_status_all')}</option>
            {stores.map(s => (
              <option key={s.auth_id} value={s.auth_id}>{s.store_name}</option>
            ))}
          </select>
        </div>
      )}
    />

      {/* ── Import Progress Modal ── */}
      {importing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {t('products.import')}...
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              {t('products.processing_rows')}
            </p>
          </div>
        </div>
      )}

      {/* ── Template Selection Modal ── */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowTemplateModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {t('products.template')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {t('products.template_select_hint')}
            </p>
            {templateLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {templateSets.map(set => (
                  <button
                    key={set.set_id}
                    onClick={() => handleTemplateSetSelect(set.set_id)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
                  >
                    <div className="font-medium text-slate-800 dark:text-slate-100">{set.set_name}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{set.set_code}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-between mt-6">
              <button
                onClick={handleTemplateAll}
                className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                {t('products.template_download_all')}
              </button>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {batchAssignIds && (
        <BatchAssignResponsibleModal
          entityType="product"
          selectedIds={batchAssignIds}
          onClose={() => setBatchAssignIds(null)}
          onSuccess={() => {
            setBatchAssignIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {batchRemoveIds && (
        <BatchRemoveResponsibleModal
          entityType="product"
          selectedIds={batchRemoveIds}
          onClose={() => setBatchRemoveIds(null)}
          onSuccess={() => {
            setBatchRemoveIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {batchUpdateIds && (
        <BatchUpdateModal
          entityType="products"
          selectedIds={batchUpdateIds}
          onClose={() => setBatchUpdateIds(null)}
          onSuccess={() => {
            setBatchUpdateIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {qcModalItem && (
        <ProductQcCheckModal
          productId={qcModalItem.entityId}
          sku={qcModalItem.sku}
          currentStatus={qcModalItem.qcStatus}
          onClose={() => setQcModalItem(null)}
          onSuccess={() => {
            setQcModalItem(null);
            setQcRefreshKey(k => k + 1);
          }}
        />
      )}

      {batchQcModal && (
        <BatchQcCheckModal
          selectedIds={batchQcModal}
          onClose={() => setBatchQcModal(null)}
          onSuccess={() => {
            setBatchQcModal(null);
            setQcRefreshKey(k => k + 1);
          }}
        />
      )}
      {pushModalProductIds !== null && (
        <StoreSelectorModal
          productIds={pushModalProductIds}
          onClose={() => setPushModalProductIds(null)}
          onRequestPush={(storeId, ids) => handleBatchPushToChannel(storeId, ids)}
        />
      )}

      {pushStatusModal && (
        <BatchPushStatusModal
          selectedIds={pushStatusModal.ids}
          storeId={pushStatusModal.storeId}
          onClose={() => setPushStatusModal(null)}
          onSuccess={() => {
            setPushStatusModal(null);
            setQcRefreshKey(k => k + 1);
          }}
        />
      )}

      {/* ── 推送进度弹窗 ── */}
      <TaskProgressModal
        isOpen={pushProgress.isOpen}
        title={pushProgress.title}
        message={pushProgress.message}
      />
    </>
  );
};

export default ProductList;
